import Vue from 'vue';
import i18n from '@/i18n';

const dialogQueue = [];
let activeResolver = null;

const DIALOG_COPY = {
  zh: {
    alertTitle: '\u63d0\u793a',
    confirmTitle: '\u8bf7\u786e\u8ba4',
    promptTitle: '\u8bf7\u8f93\u5165',
    alertButton: '\u77e5\u9053\u4e86',
    confirmButton: '\u786e\u5b9a',
    cancelButton: '\u53d6\u6d88'
  },
  en: {
    alertTitle: 'Notice',
    confirmTitle: 'Confirm',
    promptTitle: 'Input',
    alertButton: 'OK',
    confirmButton: 'Confirm',
    cancelButton: 'Cancel'
  }
};

export const dialogState = Vue.observable({
  visible: false,
  type: 'alert',
  title: '',
  message: '',
  confirmText: '',
  cancelText: '',
  showCancel: false,
  closeOnOverlay: false,
  dangerous: false,
  inputValue: '',
  placeholder: '',
  maxLength: 0
});

function getDialogCopy() {
  return i18n && i18n.locale === 'en' ? DIALOG_COPY.en : DIALOG_COPY.zh;
}

function buildDialogConfig(type, message, options = {}) {
  const copy = getDialogCopy();
  return {
    type,
    title: options.title || (type === 'confirm' ? copy.confirmTitle : type === 'prompt' ? copy.promptTitle : copy.alertTitle),
    message: message == null ? '' : String(message),
    confirmText: options.confirmText || (type === 'alert' ? copy.alertButton : copy.confirmButton),
    cancelText: options.cancelText || copy.cancelButton,
    showCancel: type !== 'alert',
    closeOnOverlay: !!options.closeOnOverlay,
    dangerous: !!options.dangerous,
    inputValue: options.defaultValue == null ? '' : String(options.defaultValue),
    placeholder: options.placeholder || '',
    maxLength: Number(options.maxLength) > 0 ? Number(options.maxLength) : 0
  };
}

function resetDialogState() {
  dialogState.visible = false;
  dialogState.type = 'alert';
  dialogState.title = '';
  dialogState.message = '';
  dialogState.confirmText = '';
  dialogState.cancelText = '';
  dialogState.showCancel = false;
  dialogState.closeOnOverlay = false;
  dialogState.dangerous = false;
  dialogState.inputValue = '';
  dialogState.placeholder = '';
  dialogState.maxLength = 0;
}

function openNextDialog() {
  if (dialogState.visible || dialogQueue.length === 0) {
    return;
  }

  const nextDialog = dialogQueue.shift();
  activeResolver = nextDialog.resolve;
  Object.assign(dialogState, nextDialog.config, { visible: true });
}

function enqueueDialog(type, message, options = {}) {
  return new Promise((resolve) => {
    dialogQueue.push({
      config: buildDialogConfig(type, message, options),
      resolve
    });
    openNextDialog();
  });
}

export function showAlert(message, options = {}) {
  return enqueueDialog('alert', message, options);
}

export function showConfirm(message, options = {}) {
  return enqueueDialog('confirm', message, options);
}

// 通用输入框弹窗，替代 window.prompt：确认返回输入值（未改动则为默认值），取消返回 null
export function showPrompt(message, defaultValue = '', options = {}) {
  return enqueueDialog('prompt', message, { ...options, defaultValue });
}

export function resolveDialog(result) {
  if (!dialogState.visible) {
    return;
  }

  const resolver = activeResolver;
  activeResolver = null;
  resetDialogState();

  if (typeof resolver === 'function') {
    resolver(result);
  }

  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(openNextDialog);
  } else {
    setTimeout(openNextDialog, 0);
  }
}

export function installGlobalDialogs() {
  if (typeof window === 'undefined' || window.__aihelpDialogInstalled) {
    return;
  }

  window.__aihelpDialogInstalled = true;
  window.__aihelpNativeAlert = typeof window.alert === 'function' ? window.alert.bind(window) : null;
  window.__aihelpNativeConfirm = typeof window.confirm === 'function' ? window.confirm.bind(window) : null;
  window.__aihelpNativePrompt = typeof window.prompt === 'function' ? window.prompt.bind(window) : null;

  window.aihelpAlert = showAlert;
  window.aihelpConfirm = showConfirm;
  window.aihelpPrompt = showPrompt;
  window.alert = (message, options) => showAlert(message, options);
  window.confirm = (message, options) => showConfirm(message, options);
  // 注意：与原生同步 API 不同，此处的 prompt 返回 Promise，调用方需 await
  window.prompt = (message, defaultValue, options) => showPrompt(message, defaultValue, options);
}
